/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
/**
 * Configuration data that is fed into the `trimText` function. It is used to calculate how many glyphs can fit per line
 * and then determine how many lines can fit per rectangle.
 */
export interface ITrimTextData {
	fontSize: number;
	fontFamily: string;
	maxWidth: number;
	maxHeight: number;
	lineHeight: number;
	extraLines: number; // Provides X-lines worth of padding along the Y-axis.
}

/**
 * We create a CanvasRenderingContext2D here because we need a "global" canvas we can use for measuring the width of drawn text and do not want 1 canvas per text object.
 * Unfortunately, measuring the dimensions of text is still pretty painful in 2025, so using the canvas to convert text elements to its pixel dimensions is still one of the easiest ways.
 */
const workingContext: CanvasRenderingContext2D = document.createElement('canvas').getContext('2d');
/**
 * This is the character we use to determine how many glyphs we can fit within a given line.
 * We are using "W" here because "W" is typically the widest glyph in a Latin language
 */
const testCharacter: string = `W`;

/**
 * Potentially slices the text to a much smaller substring if the input text is larger than the given Rectangular area
 * This step is intended to be used before text layout is calculated, so we need to do a very fast, but inaccurate calculation
 * without relying on the layout engine to determine how many characters we can reasonably fit into the given rectangle.
 * We do this by:
 * 1. Given a test glyph, measure the width of a single character given input parameters
 * 2. Calculate how many of that glyph can fit on a single line
 * 3. Calculate the maximum number of lines we can fit in the rectangle given its height (+ any padding)
 * 4. The characters per line multiplied by the total number of lines is therefore our maximum theoretical characters this rectangle can support
 * 5. If the input text is longer than the max character count supported by the rectangle, we then slice the string to fit.
 * More info: https://mocasystems.atlassian.net/browse/TP-6688
 */
export const trimText = (text: string, config: ITrimTextData): string => {
	const paddedLineHeight: number = config.lineHeight + config.extraLines * config.lineHeight;
	const paddedMaxHeight: number = config.maxHeight + paddedLineHeight;
	const maxLines: number = Math.floor(paddedMaxHeight / config.lineHeight);

	workingContext.font = `${config.fontSize}px ${config.fontFamily}`;
	const textMetrics: TextMetrics = workingContext.measureText(testCharacter);

	const maxCharactersPerLine: number = config.maxWidth / textMetrics.width;
	const totalCharacters: number = maxCharactersPerLine * maxLines;

	if (text.length <= totalCharacters) {
		return text;
	}

	return text.slice(0, totalCharacters);
};
